/**
 * EconomicSpecService
 *
 * Service layer for managing economic specifications at runtime.
 * Implements a 2-tier system:
 *
 * Tier 1 (Source): city.config.ts - base configuration (EconomicInputs)
 * Tier 2 (Override): localStorage - runtime overrides from internal form
 *
 * This service merges these tiers to provide "effective specs" to consumers.
 * Overrides take precedence over config values.
 *
 * Example:
 *   const effectiveSpecs = economicSpecService.getEffectiveSpecs('tenant-glastonbury', baseConfig);
 *   // Returns: config values merged with any saved localStorage overrides
 */

export interface EconomicInputs {
  annualConsumerSpending: string;
  annualIndustrialSpending?: string;
  projectedCommunityTreasury: string;
  projectedCityRevenue: string;
  dataSource?: {
    consumerSpending?: string;
    industrialSpending?: string;
  };
}

export interface EconomicSpecOverride {
  cityId: string;
  override: Partial<EconomicInputs>;
  timestamp: number; // milliseconds since epoch
  reason?: string; // optional note about why override was made
}

export interface EconomicChangeLogEntry {
  timestamp: number;
  override: Partial<EconomicInputs>;
  reason?: string;
  appliedBy?: string; // optional: user who made the change
}

class EconomicSpecServiceClass {
  private readonly OVERRIDE_STORAGE_KEY_PREFIX = 'economic_override_';
  private readonly CHANGELOG_STORAGE_KEY_PREFIX = 'economic_changelog_';

  /**
   * Get effective specs by merging base config with any localStorage overrides.
   * Overrides take precedence over config values.
   *
   * @param cityId - Unique identifier for the city (e.g., 'tenant-glastonbury')
   * @param baseConfig - Base economic specs from city.config.ts
   * @returns Merged effective specs (config + override)
   * @throws Error if required fields are missing from effective specs
   */
  getEffectiveSpecs(cityId: string, baseConfig: EconomicInputs): EconomicInputs {
    if (!baseConfig) {
      throw new Error(`Base config is required for city: ${cityId}`);
    }

    // Validate required fields in base config
    if (!baseConfig.annualConsumerSpending) {
      throw new Error(`Missing required field 'annualConsumerSpending' for city: ${cityId}`);
    }
    if (!baseConfig.projectedCommunityTreasury) {
      throw new Error(`Missing required field 'projectedCommunityTreasury' for city: ${cityId}`);
    }
    if (!baseConfig.projectedCityRevenue) {
      throw new Error(`Missing required field 'projectedCityRevenue' for city: ${cityId}`);
    }

    // Fetch any stored override
    const storedOverride = this.getStoredOverride(cityId);

    // If no override, return base config as-is
    if (!storedOverride) {
      return { ...baseConfig };
    }

    // Merge: base config + override (override takes precedence)
    const effectiveSpecs: EconomicInputs = {
      ...baseConfig,
      ...storedOverride.override,
      // Ensure nested dataSource is properly merged
      dataSource: {
        ...baseConfig.dataSource,
        ...(storedOverride.override.dataSource || {}),
      },
    };

    // Validate that merge didn't remove required fields
    if (!effectiveSpecs.annualConsumerSpending) {
      throw new Error(
        `Override for city ${cityId} removed required field 'annualConsumerSpending'. ` +
          `Cannot merge override with base config.`,
      );
    }

    return effectiveSpecs;
  }

  /**
   * Save an override for a specific city to localStorage.
   * Automatically adds entry to changelog.
   *
   * @param cityId - Unique identifier for the city
   * @param override - Partial specs to override (only fields provided will override)
   * @param reason - Optional note about why this override was made
   */
  saveOverride(cityId: string, override: Partial<EconomicInputs>, reason?: string): void {
    if (!cityId) {
      throw new Error('cityId is required to save override');
    }

    // SSR-safe: localStorage is not available in Node.js during prerendering
    if (typeof localStorage === 'undefined') {
      console.warn(
        `Cannot save override for city ${cityId}: localStorage is not defined (SSR environment)`,
      );
      return;
    }

    // Create override entry with timestamp
    const overrideEntry: EconomicSpecOverride = {
      cityId,
      override,
      timestamp: Date.now(),
      reason,
    };

    // Save to localStorage
    const storageKey = `${this.OVERRIDE_STORAGE_KEY_PREFIX}${cityId}`;
    try {
      localStorage.setItem(storageKey, JSON.stringify(overrideEntry));
    } catch (error) {
      console.error(`Failed to save override for city ${cityId}:`, error);
      throw new Error(
        `Failed to save override to localStorage: ${error instanceof Error ? error.message : 'Unknown error'}`,
        { cause: error },
      );
    }

    // Add to changelog
    this.addToChangeLog(cityId, override, reason);
  }

  /**
   * Retrieve the stored override for a city from localStorage.
   * Returns null if no override exists.
   *
   * @param cityId - Unique identifier for the city
   * @returns Stored override or null if none exists
   */
  getStoredOverride(cityId: string): EconomicSpecOverride | null {
    if (!cityId) {
      return null;
    }

    // SSR-safe: localStorage is not available in Node.js during prerendering
    if (typeof localStorage === 'undefined') {
      console.warn(
        `Failed to retrieve override for city ${cityId}: localStorage is not defined (SSR environment)`,
      );
      return null;
    }

    const storageKey = `${this.OVERRIDE_STORAGE_KEY_PREFIX}${cityId}`;
    try {
      const stored = localStorage.getItem(storageKey);
      if (!stored) {
        return null;
      }

      const parsed = JSON.parse(stored) as EconomicSpecOverride;
      return parsed;
    } catch (error) {
      console.error(`Failed to retrieve override for city ${cityId}:`, error);
      return null;
    }
  }

  /**
   * Clear the override for a specific city.
   * Removes from localStorage but keeps changelog for audit trail.
   *
   * @param cityId - Unique identifier for the city
   */
  clearOverride(cityId: string): void {
    if (!cityId) {
      throw new Error('cityId is required to clear override');
    }

    // SSR-safe: localStorage is not available in Node.js during prerendering
    if (typeof localStorage === 'undefined') {
      console.warn(
        `Cannot clear override for city ${cityId}: localStorage is not defined (SSR environment)`,
      );
      return;
    }

    const storageKey = `${this.OVERRIDE_STORAGE_KEY_PREFIX}${cityId}`;
    try {
      localStorage.removeItem(storageKey);
    } catch (error) {
      console.error(`Failed to clear override for city ${cityId}:`, error);
      throw new Error(
        `Failed to clear override from localStorage: ${error instanceof Error ? error.message : 'Unknown error'}`,
        { cause: error },
      );
    }

    // Note: We do NOT clear changelog, as it's an audit trail
  }

  /**
   * Get the changelog for a city (all override attempts, including current).
   * Useful for auditing what changes were made and when.
   *
   * @param cityId - Unique identifier for the city
   * @returns Array of changelog entries, oldest first
   */
  getChangeLog(cityId: string): EconomicChangeLogEntry[] {
    if (!cityId) {
      return [];
    }

    // SSR-safe: localStorage is not available in Node.js during prerendering
    if (typeof localStorage === 'undefined') {
      return [];
    }

    const storageKey = `${this.CHANGELOG_STORAGE_KEY_PREFIX}${cityId}`;
    try {
      const stored = localStorage.getItem(storageKey);
      if (!stored) {
        return [];
      }

      const parsed = JSON.parse(stored) as EconomicChangeLogEntry[];
      return parsed;
    } catch (error) {
      console.error(`Failed to retrieve changelog for city ${cityId}:`, error);
      return [];
    }
  }

  /**
   * Check if an override exists for a city.
   * Useful for UI indicators (e.g., "using override" badge).
   *
   * @param cityId - Unique identifier for the city
   * @returns true if override exists, false otherwise
   */
  hasOverride(cityId: string): boolean {
    return this.getStoredOverride(cityId) !== null;
  }

  /**
   * Get the timestamp when the current override was last saved.
   * Useful for UI display (e.g., "Last modified: Oct 18, 2025 at 2:30 PM").
   *
   * @param cityId - Unique identifier for the city
   * @returns Timestamp in milliseconds, or null if no override exists
   */
  getOverrideTimestamp(cityId: string): number | null {
    const override = this.getStoredOverride(cityId);
    return override ? override.timestamp : null;
  }

  /**
   * PRIVATE: Add entry to changelog
   */
  private addToChangeLog(cityId: string, override: Partial<EconomicInputs>, reason?: string): void {
    // SSR-safe: localStorage is not available in Node.js during prerendering
    if (typeof localStorage === 'undefined') {
      return;
    }

    const storageKey = `${this.CHANGELOG_STORAGE_KEY_PREFIX}${cityId}`;

    // Get existing changelog
    let changelog: EconomicChangeLogEntry[] = [];
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        changelog = JSON.parse(stored);
      }
    } catch (error) {
      console.error(`Failed to read existing changelog for city ${cityId}:`, error);
      changelog = [];
    }

    // Add new entry
    const entry: EconomicChangeLogEntry = {
      timestamp: Date.now(),
      override,
      reason,
    };
    changelog.push(entry);

    // Save updated changelog (keep last 100 entries to prevent unbounded growth)
    const trimmedChangelog = changelog.slice(-100);
    try {
      localStorage.setItem(storageKey, JSON.stringify(trimmedChangelog));
    } catch (error) {
      console.error(`Failed to save changelog for city ${cityId}:`, error);
    }
  }
}

// Export singleton instance
export const economicSpecService = new EconomicSpecServiceClass();
